import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
  Cell
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800/90 text-white p-3 rounded-lg shadow-xl border border-slate-700 max-w-xs z-50 backdrop-blur-sm">
        <p className="font-bold text-amber-500 mb-2 border-b border-slate-600 pb-1">{label}</p>
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span>Promedio Error:</span>
            <span className="font-mono font-bold ml-3" style={{ color: payload[0].value > 0 ? '#ef4444' : '#10b981' }}>{Number(payload[0].value).toFixed(4)}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const ChartPromedioErrorVolumen = ({ promedioErrorVolumen }) => {
  const [showInfo, setShowInfo] = useState(false);

  const data = [];
  if (promedioErrorVolumen) {
    Object.entries(promedioErrorVolumen).forEach(([fecha, valores]) => {
      if (Array.isArray(valores) && valores.length > 0) {
        const dato = valores[0];
        if (dato && dato.serie !== "null") {
          data.push({
            name: fecha,
            valor: parseFloat(dato.valor)
          });
        }
      }
    });
  }

  data.sort((a, b) => {
    const [da, ma, ya] = a.name.split('/').map(Number);
    const [db, mb, yb] = b.name.split('/').map(Number);
    return new Date(ya, ma - 1, da) - new Date(yb, mb - 1, db);
  });

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex flex-col h-[450px] relative">
      <div className="mb-6 flex justify-between items-start">
        <div className="flex items-start gap-2">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              📉 Promedio de Error Volumétrico
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Comportamiento del error promedio a lo largo del tiempo.
              <span className="text-blue-500 text-xs ml-2">(Zoom disponible)</span>
            </p>
          </div>

          <button
            onClick={() => setShowInfo(!showInfo)}
            className="mt-1 text-slate-400 hover:text-blue-600 transition-colors"
            title="Ver fórmula de cálculo"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </button>

          {showInfo && (
            <div className="absolute top-14 left-4 z-50 bg-slate-800 text-white p-4 rounded-xl shadow-2xl border border-slate-700 w-72 animated fadeIn">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-amber-500 text-sm">Fórmula de Cálculo</h4>
                <button onClick={() => setShowInfo(false)} className="text-slate-400 hover:text-white"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Calcula el promedio aritmético de los errores porcentuales de todas las calibraciones realizadas en la fecha.
              </p>
              <div className="mt-3 text-xs bg-slate-900 p-2 rounded border border-slate-700 font-mono text-center">
                Promedio = Σ(Errores) / N
              </div>
            </div>
          )}
        </div>
        <div className="px-3 py-1 bg-green-50 text-green-700 text-xs font-bold rounded-full uppercase tracking-wider">
          Tendencia
        </div>
      </div>

      <div className="flex-grow overflow-x-auto">
        <div className="min-w-[700px] h-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
            >
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: "#64748b" }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
                dy={10}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                tickFormatter={(value) => value.toFixed(3)}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#94a3b8" />
              <Bar
                dataKey="valor"
                name="Error Promedio"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
                barSize={30}
              />
              <Brush
                dataKey="name"
                height={30}
                stroke="#64748b"
                travellerWidth={10}
                fill="#f8fafc"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ChartPromedioErrorVolumen;
